import { useState } from "react";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ambulance, ArrowRight, Building2, Heart, ShieldCheck } from "lucide-react";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";

export default function EmergencyLanding() {
  usePageTitle(
    "Emergency Fundraising | HealthWallet",
    "Community-powered emergency healthcare funding for the Minneapolis–St. Paul area."
  );

  const [, setLocation] = useLocation();
  const [shareInput, setShareInput] = useState("");

  const handleFind = () => {
    const input = shareInput.trim();
    if (!input) return;
    // Accept a full URL like https://…/emergency/TOKEN or just the token itself
    const match = input.match(/\/emergency\/([A-Za-z0-9_-]+)/);
    const token = match ? match[1] : input;
    setLocation(`/emergency/${token}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <img
            src={healthWalletLogo}
            alt="HealthWallet"
            className="h-8 w-8"
            width="1024"
            height="1024"
          />
          <span className="font-semibold text-lg">HealthWallet</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 space-y-10">
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <div className="p-3 rounded-full bg-destructive/10">
              <Ambulance className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">Emergency Fundraising</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Community-powered emergency healthcare funding for the Minneapolis–St. Paul area. When
            unexpected medical bills hit, your community can help.
          </p>
        </div>

        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Have a share link?</CardTitle>
            <CardDescription>
              Paste the link or share token you received to view and contribute to an emergency
              campaign.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="input-share-link">Share link or token</Label>
              <Input
                id="input-share-link"
                data-testid="input-share-link"
                placeholder="https://healthwallet.ai/emergency/… or token"
                value={shareInput}
                onChange={(e) => setShareInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleFind()}
              />
            </div>
            <Button
              className="w-full"
              onClick={handleFind}
              disabled={!shareInput.trim()}
              data-testid="button-find-campaign"
            >
              View Campaign
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>

        <div className="grid sm:grid-cols-3 gap-4 pt-4">
          <Card>
            <CardContent className="pt-6 space-y-2 text-center">
              <Building2 className="h-7 w-7 mx-auto text-muted-foreground" />
              <h3 className="font-semibold text-sm">Hospital-Initiated</h3>
              <p className="text-xs text-muted-foreground">
                Emergency campaigns are created by partner hospitals and clinics on behalf of
                patients who need immediate community support.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 space-y-2 text-center">
              <Heart className="h-7 w-7 mx-auto text-muted-foreground" />
              <h3 className="font-semibold text-sm">Community-Funded</h3>
              <p className="text-xs text-muted-foreground">
                Friends, family, and community members contribute directly to the patient's care
                fund. Every dollar goes toward verified medical costs.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 space-y-2 text-center">
              <ShieldCheck className="h-7 w-7 mx-auto text-muted-foreground" />
              <h3 className="font-semibold text-sm">Secure &amp; Verified</h3>
              <p className="text-xs text-muted-foreground">
                All campaigns are verified by the treating healthcare provider. Contributions are
                processed securely via Stripe.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="text-center pt-4 space-y-2">
          <p className="text-sm text-muted-foreground">
            Are you a healthcare provider? Emergency campaigns are created through the{" "}
            <a href="/hospital-partners" className="underline">
              Hospital Partner Portal
            </a>
            .
          </p>
          <p className="text-sm text-muted-foreground">
            Questions?{" "}
            <a href="/about" className="underline">
              Learn more about HealthWallet
            </a>
            .
          </p>
        </div>
      </main>

      <footer className="border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-muted-foreground space-y-1">
          <p>HealthWallet is a community-powered healthcare funding platform serving the Minneapolis–St. Paul area.</p>
          <p className="text-xs">
            HealthWallet provides technology services only. All funds are processed and held by Stripe, Inc.{" "}
            <a href="/terms" className="underline">Terms</a> |{" "}
            <a href="/privacy" className="underline">Privacy</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
